import { useMemo } from 'react';
import {
  f0,
  fc,
  mk,
  moIncome,
  moPaid,
  moVar,
  moSavings,
  calcPayoff,
  getAllBills,
  MOS,
  getBudgetMode,
  getVarBudget,
  getSpendingTrends,
  getAnnualSummary,
  getAllCats,
  getBudgetStreak,
  getSavingsStreak,
  getHealthScore,
  getEarnedMilestones,
  getNetWorthHistory,
  getSpendingInsights,
  getFinancialTips,
  getCashFlow,
  getCashFlowForecast,
} from '../../utils';

/**
 * useDashboardData — derives every computed value the Dashboard needs from raw state.
 * Keeps Dashboard.jsx a thin composition layer with no inline math.
 *
 * Call once at the top of Dashboard; destructure only what each card needs.
 */
export function useDashboardData(state) {
  const {
    cards,
    fixedBills,
    extra,
    method,
    lump,
    savContrib,
    savGoal,
    savLog,
    incomeLog,
    paidBills,
    varSpend,
    savDeposits,
    budgetSlider,
    customCats,
    extraGoals,
    checkins,
    recapSeen,
    incomeMode,
    monthlyIncome,
  } = state;

  const cats = getAllCats(customCats, state.disabledCats);
  const now = new Date();
  const k = mk(now.getFullYear(), now.getMonth());
  const pd = paidBills[k] || {};

  // Expensive payoff calculations — memoized
  const r = useMemo(() => calcPayoff(cards, extra, method, lump), [cards, extra, method, lump]);
  const snow = useMemo(() => calcPayoff(cards, extra, 'snowball'), [cards, extra]);
  const aval = useMemo(() => calcPayoff(cards, extra, 'avalanche'), [cards, extra]);

  // Debt totals
  const totBal = (cards || []).reduce((s, c) => s + +c.bal, 0);
  const avgAPR = cards?.length ? cards.reduce((s, c) => s + c.apr, 0) / cards.length : 0;
  const daily = (totBal * (avgAPR / 100)) / 365;

  // Monthly cash figures
  const inc = incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog, k);
  const paid = moPaid(paidBills, k, cards, fixedBills, state.subscriptions);
  const vari = moVar(varSpend, k);
  const savA = moSavings(savDeposits, k, savContrib);
  const allBills = getAllBills(cards, fixedBills, state.subscriptions);
  const spent = paid + vari + savA;
  const rem = inc - spent;
  const rc = rem >= 0 ? 'var(--grn)' : 'var(--red)';
  const pct = inc > 0 ? Math.min(100, Math.max(0, (spent / inc) * 100)).toFixed(1) : 0;

  // Discretionary budget
  const mode = getBudgetMode(budgetSlider ?? 50);
  const slider = budgetSlider ?? 50;
  const { budget: baseBudget } = getVarBudget(inc, allBills, savA, slider);
  // Carryover: if enabled, add any unspent budget from last month
  const lastMoDateForCarry = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMoKeyForCarry = mk(lastMoDateForCarry.getFullYear(), lastMoDateForCarry.getMonth());
  const carryoverAmt = state.budgetCarryover
    ? (() => {
        const lastInc = incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog, lastMoKeyForCarry);
        const { budget: lastBudget } = getVarBudget(lastInc, allBills, moSavings(savDeposits, lastMoKeyForCarry, savContrib), slider);
        const lastSpent = moVar(varSpend, lastMoKeyForCarry);
        return Math.max(0, lastBudget - lastSpent);
      })()
    : 0;
  const varBudget = baseBudget + carryoverAmt;
  const budgetRem = varBudget - vari;
  const budgetColor = budgetRem >= 0 ? 'var(--grn)' : 'var(--red)';
  const budgetPct = varBudget > 0 ? Math.min(100, (vari / varBudget) * 100).toFixed(1) : 0;

  // Savings — extraGoals is the primary source when goals are set up; savLog is the
  // legacy single-goal check-in system. Using extraGoals total avoids showing $0 on
  // the dashboard when the user only interacts with the Savings tab.
  const extraGoalsTotal = (extraGoals || []).reduce((s, g) => s + (g.bal || 0), 0);
  const lat = extraGoalsTotal > 0 ? extraGoalsTotal : (savLog?.length ? savLog[0].bal : 0);
  const sg = savGoal || 1000;
  const sp = Math.min(100, Math.round((lat / sg) * 100));

  // Financial position (savings − debt)
  const netWorth = lat - totBal;
  const nwColor = netWorth >= 0 ? 'var(--grn)' : 'var(--red)';
  const fNet = (n) => (n >= 0 ? f0(n) : '−' + f0(Math.abs(n)));

  // Bills due soon
  const today = now.getDate();
  const dueSoon = allBills.filter((b) => b.due >= today && b.due <= today + 7 && !pd[b.id]);
  const dueThisWeek = allBills.filter((b) => b.due >= today && b.due <= today + 7);

  // Annual subscriptions due in the next 30 days
  const annualDueSoon = (state.subscriptions || [])
    .filter((s) => s.cycle === 'annual' && s.amt > 0)
    .map((s) => {
      const dueMonth = (s.dueMonth !== null && s.dueMonth !== undefined ? s.dueMonth : now.getMonth() + 1) - 1;
      const d = new Date(now.getFullYear(), dueMonth, s.due || 1);
      if (d < now) d.setFullYear(d.getFullYear() + 1);
      const daysUntil = Math.ceil((d - now) / 86400000);
      return { ...s, daysUntil, dueDate: d };
    })
    .filter((s) => s.daysUntil <= 30 && s.daysUntil >= 0)
    .sort((a, b) => a.daysUntil - b.daysUntil);

  // Payoff comparison
  const intDiff = Math.abs(snow.int - aval.int);
  const moDiff = Math.abs(snow.mos - aval.mos);
  const firstS = cards?.length ? [...cards].sort((a, b) => a.bal - b.bal)[0] : null;

  // Spending trends
  const trends = getSpendingTrends(varSpend, 6, cats);
  const hasTrends = trends.some((t) => t.total > 0);

  // Streaks
  const budgetStreak = getBudgetStreak(
    incomeLog,
    varSpend,
    savDeposits,
    cards || [],
    fixedBills || [],
    savContrib,
    budgetSlider,
    incomeMode,
    monthlyIncome,
    state.subscriptions || []
  );
  const savingsStreak = getSavingsStreak(savDeposits);

  // Emergency fund coverage
  const monthlyExpenses = allBills.reduce((s, b) => s + b.amt, 0) + (vari || 0) + (savA || 0);
  const efCoverage =
    lat > 0 && monthlyExpenses > 0 ? Math.round((lat / monthlyExpenses) * 10) / 10 : null;

  // Smart nudge
  const nudge = (() => {
    if (totBal === 0 && lat > 0)
      return {
        msg: `You're debt-free! Financial position: ${f0(netWorth)}`,
        color: 'var(--grn)',
        em: '🎉',
      };
    const highAPR = [...(cards || [])].sort((a, b) => b.apr - a.apr)[0];
    if (inc > 0 && varBudget > 0 && budgetRem > 50 && highAPR)
      return {
        msg: `${fc(budgetRem)} left this month — putting it toward ${highAPR.name} saves ~${fc(budgetRem * (highAPR.apr / 100 / 12) * 12)}/yr in interest.`,
        color: 'var(--grn)',
        em: '💡',
      };
    if (lat > 0 && lat >= sg * 0.8 && lat < sg)
      return {
        msg: `${Math.round((lat / sg) * 100)}% of the way to your savings goal — only ${fc(sg - lat)} to go!`,
        color: 'var(--ylw)',
        em: '🎯',
      };
    if (method === 'snowball' && intDiff > 200)
      return {
        msg: `Switching to Avalanche payoff would save you an extra ${f0(intDiff)} in interest.`,
        color: 'var(--blu)',
        em: '💡',
      };
    return null;
  })();

  // Savings history chart data
  const savChartData = [...(savLog || [])].reverse().map((e) => ({ lbl: e.mo, bal: e.bal }));

  // Year-to-date summary
  const ytd = getAnnualSummary(
    incomeLog,
    paidBills,
    varSpend,
    savDeposits,
    cards || [],
    fixedBills || [],
    savContrib,
    now.getFullYear(),
    incomeMode,
    monthlyIncome,
    state.subscriptions || []
  );

  // Health score
  const health = getHealthScore(state);

  // Spending forecast — only meaningful once there are at least 5 days of data.
  // Before day 5 a single large purchase would project a wildly inflated month total.
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dayOfMonth = now.getDate();
  const forecast = dayOfMonth >= 5 && vari > 0 ? Math.round((vari / dayOfMonth) * daysInMonth) : 0;

  // Misc
  const milestones = getEarnedMilestones(state);
  const nwHistory = getNetWorthHistory(savLog, checkins, cards, state.extraGoals);
  const insights = getSpendingInsights(varSpend, customCats, 3, state.disabledCats);
  const tips = getFinancialTips(state);

  // Cash flow + contextual tip
  const cf = getCashFlow(state);
  const cashFlowTip = (() => {
    if (!cf.inc) return null;
    const highAPR = [...(cards || [])].filter((c) => c.bal > 0).sort((a, b) => b.apr - a.apr)[0];
    const nearGoal = [...(state.extraGoals || [])]
      .filter((g) => g.target > 0 && g.bal < g.target)
      .sort((a, b) => b.bal / b.target - a.bal / a.target)[0];
    if (cf.leftOver > 0 && highAPR?.apr > 10)
      return {
        msg: `Put extra toward ${highAPR.name} (${highAPR.apr.toFixed(1)}% APR) to cut interest costs`,
        c: 'var(--grn)',
      };
    if (cf.leftOver > 0 && nearGoal) {
      const nearPct = Math.round((nearGoal.bal / nearGoal.target) * 100);
      return {
        msg: `${nearPct}% toward ${nearGoal.name} — ${f0(Math.max(0, nearGoal.target - nearGoal.bal))} to go`,
        c: 'var(--ylw)',
      };
    }
    if (cf.leftOver < 0)
      return {
        msg: `Planned spending exceeds income by ${f0(Math.abs(cf.leftOver))}`,
        c: 'var(--red)',
      };
    return null;
  })();

  // Cash flow forecast — projected paydays + upcoming bills over the next 45
  // days, with a running balance from the user's current bank balance.
  const cashFlowForecast = getCashFlowForecast(state, 45);

  // Net worth trajectory — project 12 months forward using current monthly savings rate
  // and debt payoff schedule from calcPayoff.
  const netWorthTrajectory = (() => {
    if (inc === 0) return null;
    // Monthly net worth delta: savings added per month minus debt reduced per month
    const monthlySavingsAdd = savA || savContrib || 0;
    // Debt reduction per month from the payoff schedule (first month payment minus interest)
    const firstSchedRow = r.sched[0];
    const monthlyDebtReduction = firstSchedRow ? Math.max(0, totBal - firstSchedRow.tot) : 0;
    const monthlyNWDelta = monthlySavingsAdd + monthlyDebtReduction;
    if (monthlyNWDelta <= 0) return null;
    const monthsToProject = 12;
    const projectedNW = netWorth + monthlyNWDelta * monthsToProject;
    const targetDate = new Date(now.getFullYear(), now.getMonth() + monthsToProject, 1);
    const targetLabel = targetDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    return { projectedNW, monthlyNWDelta, targetLabel, months: monthsToProject };
  })();

  // Last month recap
  const lastMoDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMoKey = mk(lastMoDate.getFullYear(), lastMoDate.getMonth());
  const lastMoLabel = MOS[lastMoDate.getMonth()] + ' ' + lastMoDate.getFullYear();
  const lastMoInc = incomeMode === 'monthly' ? monthlyIncome || 0 : moIncome(incomeLog, lastMoKey);
  const lastMoPaid = moPaid(paidBills, lastMoKey, cards, fixedBills, state.subscriptions);
  const lastMoVar = moVar(varSpend, lastMoKey);
  const lastMoSav = moSavings(savDeposits, lastMoKey, savContrib);
  const lastMoRem = lastMoInc - (lastMoPaid + lastMoVar + lastMoSav);
  const showRecap = dayOfMonth <= 10 && lastMoInc > 0 && !(recapSeen || {})[lastMoKey];

  return {
    cats,
    now,
    k,
    pd,
    r,
    snow,
    aval,
    totBal,
    avgAPR,
    daily,
    inc,
    paid,
    vari,
    savA,
    allBills,
    spent,
    rem,
    rc,
    pct,
    mode,
    slider,
    varBudget,
    budgetRem,
    budgetColor,
    budgetPct,
    carryoverAmt,
    lat,
    sg,
    sp,
    netWorth,
    nwColor,
    fNet,
    today,
    dueSoon, dueThisWeek, annualDueSoon,
    intDiff,
    moDiff,
    firstS,
    trends,
    hasTrends,
    budgetStreak,
    savingsStreak,
    efCoverage,
    nudge,
    savChartData,
    ytd,
    health,
    daysInMonth,
    dayOfMonth,
    forecast,
    milestones,
    nwHistory,
    insights,
    tips,
    cf,
    cashFlowTip,
    cashFlowForecast,
    netWorthTrajectory,
    lastMoKey,
    lastMoLabel,
    lastMoInc,
    lastMoPaid,
    lastMoVar,
    lastMoSav,
    lastMoRem,
    showRecap,
  };
}
